import React, { useState, useEffect, useCallback, useRef } from 'react';
import WaitingRoom from './WaitingRoom';
import Redaction from './Redaction';
import SessionFooter from '../composants/SessionFooter';
import UserAvatar from '../Menu/UserAvatar';
import { serverPHP, debug_mode, time_waitingroom_default, time_redaction_default, time_evaluation_default } from '../const/consts';
import Evaluation from './Evaluation';
import ResultatFinal from './ResultatFinal';
import { getTachePlan } from './FonctionsPlan';
import { useNavigate } from 'react-router-dom';
import Modal, { PodiumModal } from '../composants/Modal';
import SlidePanels from '../Menu/SlidePanels';
import ErrorHandler from '../composants/ErrorHandler';

const Session = ({ 
  user, 
  setUser, 
  derniereMaj, 
  setDerniereMaj, 
  isAdmin, 
  setIsAdmin, 
  setModalContent, 
  setModalTitle, 
  setShowModal, 
  motDePasse, 
  setMotDePasse,
  currentPhase,
  setCurrentPhase,
  setGoNext,
  setCloseSession,
  onEndSessionChange
}) => {
  const [usersInRoom, setUsersInRoom] = useState([]);
  const [inputPass, setInputPass] = useState("");
  const navigate = useNavigate();

  const [needNewPassword, setNeedNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const [time_waitingroom, setTime_waitingroom] = useState(time_waitingroom_default * 60);
  const [time_redaction, setTime_redaction] = useState(time_redaction_default * 60);
  const [time_evaluation, setTime_evaluation] = useState(time_evaluation_default * 60);

  const [isLoaded, setIsLoaded] = useState(false);
  const [sujet, setSujet] = useState('');
  const [endSession, setEndSession] = useState(false);

  const [tache, setTache] = useState("");
  const [isBanned, setIsBanned] = useState(false);

  const [parametres, setParametres] = useState({});
  const [type, setType] = useState('');
  const [redactionLongue, setRedactionLongue] = useState(false);
  const [pastContributions, setPastContributions] = useState([]);
  const [pastContributionsLoading, setPastContributionsLoading] = useState(false);

  // États pour la modal de podium automatique
  const [showAutoPodium, setShowAutoPodium] = useState(false);
  const [autoPodiumSessionInfo, setAutoPodiumSessionInfo] = useState(null);
  const [userPresenceHistory, setUserPresenceHistory] = useState(new Set());
  const [lastEvaluationPhase, setLastEvaluationPhase] = useState(null);
  
  // Réf pour éviter d'afficher plusieurs fois le même podium
  const shownPodiums = useRef(new Set());
  
  // État pour tracker si on vient juste de changer de phase
  const [phaseChangeInProgress, setPhaseChangeInProgress] = useState(false);

  const fetchTimeoutRef = useRef(null);

  useEffect(() => {
    if (onEndSessionChange) {
      onEndSessionChange(endSession);
    }
  }, [endSession, onEndSessionChange]);

  useEffect(() => {
    const newTache = getTachePlan(currentPhase);
    setTache(newTache);
  }, [currentPhase]);

  // Logique de présence de l'utilisateur pour les phases d'évaluation
  useEffect(() => {
    console.log('🔍 Phase change detected:', { currentPhase, userRole: user.role });
    
    if (currentPhase > 0 && currentPhase % 2 === 0) {
      const canEvaluateInThisPhase = user.role === 0 || user.role === 1;
      
      console.log('📊 Evaluation phase detected:', { phase: currentPhase, canEvaluate: canEvaluateInThisPhase });
      
      if (canEvaluateInThisPhase) {
        setUserPresenceHistory(prev => {
          const newSet = new Set(prev);
          newSet.add(currentPhase);
          console.log('✅ User marked as present in evaluation phase:', currentPhase, 'History:', Array.from(newSet));
          return newSet;
        });
        setLastEvaluationPhase(currentPhase);
      }
    }
  }, [currentPhase, user.role]);

  // Fonction pour récupérer les contributions passées
  const fetchPastContributions = useCallback(async () => {
    if (currentPhase < 2) return;
    
    setPastContributionsLoading(true);
    
    try {
      const response = await fetch(`${serverPHP}getContributions.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: user.currentsession,
          user_id: user.id,
          phase: currentPhase,
          action: 'final'
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.past_contributions) {
        setPastContributions(data.past_contributions);
        console.log('Past contributions loaded for podium logic:', data.past_contributions);
      } else {
        console.log('No past contributions found or error:', data);
      }
    } catch (error) {
      console.error('Error fetching past contributions:', error);
    } finally {
      setPastContributionsLoading(false);
    }
  }, [user.currentsession, user.id, currentPhase]);

  // Détecter le changement de phase et lancer le fetch
  useEffect(() => {
    setPhaseChangeInProgress(true);
    fetchPastContributions();
  }, [currentPhase, fetchPastContributions]);

  // Logique de podium automatique qui attend la fin du loading
  useEffect(() => {
    // Attendre que le loading soit terminé et qu'on ne soit pas en cours de changement de phase
    if (pastContributionsLoading || phaseChangeInProgress) {
      return;
    }

    // Maintenant on peut procéder à la logique de podium
    setPhaseChangeInProgress(false);

    console.log('🏆 Podium Effect Triggered (after loading):', {
      currentPhase,
      endSession,
      lastEvaluationPhase,
      pastContributionsCount: pastContributions.length,
      shownPodiums: Array.from(shownPodiums.current),
      loading: pastContributionsLoading
    });

    let relevantEvaluationPhase = null;
    let redactionPhaseForPodium = null;

    // CAS 1: On est dans une phase de rédaction (impaire) - podium de la phase d'évaluation précédente
    if (currentPhase % 2 === 1 && currentPhase > 0) {
      relevantEvaluationPhase = currentPhase - 1;
      redactionPhaseForPodium = relevantEvaluationPhase - 1;
      console.log('🏆 Case 1: Redaction phase, showing podium for previous evaluation');
    }
    
    // CAS 2: On arrive à l'écran de fin - podium de la dernière phase d'évaluation
    else if (endSession && lastEvaluationPhase && lastEvaluationPhase % 2 === 0) {
      relevantEvaluationPhase = lastEvaluationPhase;
      redactionPhaseForPodium = relevantEvaluationPhase - 1;
      console.log('🏆 Case 2: End session, showing podium for last evaluation phase');
    }
    
    // Si aucun cas ne s'applique, sortir
    if (!relevantEvaluationPhase || !redactionPhaseForPodium) {
      console.log('🏆 Podium check failed: No relevant evaluation phase found');
      return;
    }

    // Avons-nous déjà affiché le podium pour cette phase d'évaluation ?
    if (shownPodiums.current.has(relevantEvaluationPhase)) {
      console.log('🏆 Podium check skipped: Already shown for evaluation phase', relevantEvaluationPhase);
      return;
    }

    // L'utilisateur était-il présent pendant cette phase d'évaluation ?
    if (!userPresenceHistory.has(relevantEvaluationPhase)) {
      console.log('🏆 Podium check failed: User was not present for evaluation phase', relevantEvaluationPhase);
      return;
    }

    console.log(`🏆 Will show podium for redaction phase ${redactionPhaseForPodium} with evaluations from phase ${relevantEvaluationPhase}`);
    
    // Préparer les informations pour le fetch automatique dans la modal
    setAutoPodiumSessionInfo({
      sessionId: user.currentsession,
      userId: user.id,
      targetPhase: redactionPhaseForPodium
    });
    setShowAutoPodium(true);

    // Et on marque ce podium comme "vu" pour ne pas le ré-afficher.
    shownPodiums.current.add(relevantEvaluationPhase);
    console.log('🏆 Marked podium as shown for evaluation phase', relevantEvaluationPhase);
  
  }, [pastContributions, pastContributionsLoading, phaseChangeInProgress, currentPhase, endSession, lastEvaluationPhase, userPresenceHistory]);

  // Réinitialiser les historiques lors des changements de session
  useEffect(() => {
    setUserPresenceHistory(new Set());
    setLastEvaluationPhase(null);
    setPhaseChangeInProgress(false);
    setAutoPodiumSessionInfo(null);
    shownPodiums.current.clear();
    console.log('🔄 Reset all histories for new session/mount');
  }, [user.currentsession]);

  const closeSessionFunc = async () => {
    const session_id = user.currentsession;
    const admin_id = user.id;

    try {
      const response = await fetch(`${serverPHP}closeSession.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id, admin_id }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        if (debug_mode) console.log("Session fermée par l'administrateur");
        const newEndSession = true;
        setEndSession(newEndSession);
        if (onEndSessionChange) {
          onEndSessionChange(newEndSession);
        }
      } else {
        console.error('Erreur:', data.error);
        setError({
          type: 'SERVER_ERROR',
          message: 'Impossible de fermer la session',
          details: data.error
        });
      }
    } catch (error) {
      console.error('Erreur lors de la requête:', error);
      setError({
        type: 'NETWORK_ERROR',
        message: 'Erreur lors de la fermeture de la session',
        details: error.message
      });
    }
  };

  const goNextFunc = useCallback((forceAdmin = false) => {
    if (debug_mode) console.log("Envoyé à updateSessionPhase", JSON.stringify({
      session_id: user.currentsession,
      anciennePhase: currentPhase,
      userId: user.id,
      forceAdmin: forceAdmin
    }));
    
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        setError({
          type: 'TIMEOUT',
          message: 'La mise à jour de la phase a pris trop de temps'
        });
        reject(new Error('Timeout'));
      }, 15000);

      fetch(`${serverPHP}updateSessionPhase.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: user.currentsession,
          anciennePhase: currentPhase,
          userId: user.id,
          forceAdmin: forceAdmin
        })
      })
        .then(response => {
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          if (debug_mode) console.log("data renvoyées", data);
          if (data.success) {

            // --- NOUVELLE LOGIQUE POUR LA MODALE ---
            // Si la phase a été sautée ET que l'utilisateur est un rédacteur, on affiche la modale.
            if (data.phase_skipped && user.role === 0) {
              setModalTitle("Information sur la phase");
              setModalContent(
                  <div className="text-center">
                      <p>Une seule contribution a été proposée pendant la phase précédente.</p>
                      <p className="mt-2 text-secondary">Comme il n'y a pas lieu de l'évaluer, nous passons directement à l'étape de rédaction suivante.</p>
                  </div>
              );
              setShowModal(true);
            }

            if (data.nextphase) {
              setUser(prevUser => ({ ...prevUser, isready: false }));
              setUsersInRoom(prevUsers => {
                const updatedUsers = prevUsers.map(u => ({ ...u, isReady: false }));
                return updatedUsers;
              });
              setCurrentPhase(data.nouvellePhase);
            }

            if (data.close) {
              if (debug_mode) console.log("Fermeture reçue de updateSessionPhase");
              const newEndSession = true;
              setEndSession(newEndSession);
              if (onEndSessionChange) {
                onEndSessionChange(newEndSession);
              }
            }

            resolve(data);
          } else {
            console.error('Erreur lors de la mise à jour de la phase:', data.error);
            setError({
              type: 'SERVER_ERROR',
              message: 'Erreur lors de la mise à jour de la phase',
              details: data.error
            });
            reject(data.error);
          }
        })
        .catch(error => {
          clearTimeout(timeoutId);
          console.error('Erreur lors de la requête de mise à jour de la phase:', error);
          
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            setError({
              type: 'NETWORK_ERROR',
              message: 'Impossible de contacter le serveur pour mettre à jour la phase'
            });
          } else {
            setError({
              type: 'SERVER_ERROR',
              message: 'Erreur lors de la mise à jour de la phase',
              details: error.message
            });
          }
          reject(error);
        });
    });
  }, [currentPhase, user.currentsession, user.role, setUser, setUsersInRoom, setCurrentPhase, setEndSession, onEndSessionChange, setModalContent, setModalTitle, setShowModal]);

  useEffect(() => {
    setGoNext(() => goNextFunc);
    setCloseSession(() => closeSessionFunc);
  }, [goNextFunc, setGoNext, setCloseSession]);

  useEffect(() => {
    if (currentPhase >= 19) {
      closeSessionFunc();
    }
  }, [currentPhase, closeSessionFunc]);

  const fetchSessionData = async () => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    try {
      setError(null);
      setPasswordError(""); // Effacer les erreurs de mot de passe précédentes

      const timeoutId = setTimeout(() => {
        throw new Error('TIMEOUT');
      }, 10000);

      const response = await fetch(`${serverPHP}getSession.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: user.currentsession,
          user_id: user.id,
          motDePasse: motDePasse
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('SESSION_NOT_FOUND');
        } else if (response.status >= 500) {
          throw new Error('SERVER_ERROR');
        } else {
          throw new Error(`HTTP_ERROR_${response.status}`);
        }
      }

      const data = await response.json();
      
      if (data.success) {
        setNeedNewPassword(false);
        setPasswordError(""); // Succès - effacer toute erreur
        setIsAdmin(data.isadmin);
        setType(data.type);
        setParametres(data.parametres);
        setRedactionLongue(data.redaction_longue);
        setSujet(data.sujet);
        setRetryCount(0);
        
        if (debug_mode) console.log("résultat de getSession", data);
        if (data.time_waitingroom != null) setTime_waitingroom(data.time_waitingroom);
        if (data.time_redaction != null) setTime_redaction(data.time_redaction);
        if (data.time_evaluation != null) setTime_evaluation(data.time_evaluation);

        if (data.info && data.info.titre !== null && data.info.contenu !== null) {
          setModalTitle(data.info.titre);
          setModalContent(<div className="text-center">{data.info.contenu}</div>);
          setShowModal(true);
        }

        setCurrentPhase(data.phase);
        
        if (data.closed === 1) {
          if (debug_mode) console.log("Session fermée détectée");
          const newEndSession = true;
          setEndSession(newEndSession);
          if (onEndSessionChange) {
            onEndSessionChange(newEndSession);
          }
        } else {
          const newEndSession = false;
          setEndSession(newEndSession);
          if (onEndSessionChange) {
            onEndSessionChange(newEndSession);
          }
          setDerniereMaj(data.dernieremaj);
          setUser({ ...user, isready: false });
        }

        setIsLoaded(true);
        
      } else if (data.error === 'password') {
        if (motDePasse === "") {
          // Première fois - demander le mot de passe
          setNeedNewPassword(true);
          setPasswordError("");
        } else {
          // Mot de passe incorrect - afficher l'erreur et permettre une nouvelle saisie
          setInputPass(""); 
          setNeedNewPassword(true);
          setPasswordError("❌ Mot de passe incorrect. Veuillez réessayer.");
        }
        setIsLoaded(true);
        
      } else if (data.error === 'banned') {
        setError({
          type: 'USER_BANNED',
          sessionId: user.currentsession
        });
        setIsLoaded(true);
        
      } else {
        console.error('Erreur:', data.error);
        setError({
          type: 'VALIDATION_ERROR',
          message: data.error,
          sessionId: user.currentsession
        });
        setIsLoaded(true);
      }
      
    } catch (error) {
      console.error('Failed to fetch session info:', error);
      
      if (error.message === 'TIMEOUT') {
        setError({
          type: 'TIMEOUT',
          sessionId: user.currentsession
        });
      } else if (error.message === 'SESSION_NOT_FOUND') {
        setError({
          type: 'SESSION_NOT_FOUND',
          sessionId: user.currentsession
        });
      } else if (error.message === 'SERVER_ERROR') {
        setError({
          type: 'SERVER_ERROR',
          sessionId: user.currentsession
        });
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError({
          type: 'NETWORK_ERROR',
          sessionId: user.currentsession
        });
      } else {
        setError({
          type: 'UNKNOWN_ERROR',
          message: error.message,
          sessionId: user.currentsession,
          stack: error.stack
        });
      }
      
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    fetchSessionData();

    return () => {
      setUser(prevUser => ({ ...prevUser, isready: false }));
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [user.currentsession, currentPhase, motDePasse]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      navigator.sendBeacon(`${serverPHP}updateUser.php`, JSON.stringify({ ...user, currentsession: null }));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user]);

  const getDurationForPhase = () => {
    if (currentPhase === 0) return time_waitingroom;
    if (currentPhase % 2 !== 0) return time_redaction;
    return time_evaluation;
  };

  const isRedactionPhase = () => currentPhase % 2 !== 0;

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setIsLoaded(false);
      setError(null);
      
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      setTimeout(() => {
        fetchSessionData();
      }, delay);
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (error) {
    return (
      <ErrorHandler
        error={error}
        sessionId={user.currentsession}
        onRetry={retryCount < maxRetries ? handleRetry : null}
        onGoHome={handleGoHome}
        showRetry={retryCount < maxRetries}
      />
    );
  }

  if (!isLoaded) {
    return (
      <></>
    );
  }

  if (needNewPassword) {
    return (
      <div className="card text-center">
        <h2 className="card-title">🔒 Session protégée</h2>
        <p className="mb-4">La session <strong>{user.currentsession}</strong> est protégée par un mot de passe.</p>
        
        {/* Message d'erreur si mot de passe incorrect */}
        {passwordError && (
          <div className="card" style={{ background: 'var(--danger-color)', color: 'white', marginBottom: '1rem' }}>
            <div className="flex items-center gap-2 justify-center">
              <span>{passwordError}</span>
            </div>
          </div>
        )}
        
        <div className="form-group">
          <div className="flex gap-2">
            <input
              type="password"
              value={inputPass}
              onChange={(e) => {
                setInputPass(e.target.value);
                // Effacer l'erreur dès que l'utilisateur tape
                if (passwordError) {
                  setPasswordError("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setMotDePasse(inputPass);
                }
              }}
              placeholder="Mot de passe"
              className="form-input"
              autoFocus
            />
            <button
              className="btn"
              onClick={() => {
                setMotDePasse(inputPass);
              }}
            >
              🔓 Accéder
            </button>
          </div>
        </div>
        <div className="mt-4">
          <button className="btn btn-secondary" onClick={handleGoHome}>
            🏠 Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (endSession) {
    return (
      <>
        <ResultatFinal
          user={user}
          sujet={sujet}
          currentPhase={currentPhase}
          setModalContent={setModalContent}
          setModalTitle={setModalTitle}
          setShowModal={setShowModal}
          parametres={parametres}
          type={type}
          redactionLongue={redactionLongue}
        />
        
        {/* Podium modal aussi disponible sur l'écran de fin */}
        {showAutoPodium && (
          <PodiumModal
            sessionInfo={autoPodiumSessionInfo}
            onClose={() => setShowAutoPodium(false)}
            type={type}
          />
        )}
      </>
    );
  }

  return (
    <>
      {currentPhase === 0 && (
        <div className="subject-display">
          <span>📝</span>
          <strong>Sujet :</strong> {sujet}
        </div>
      )}

      <div className="session-content">
        {currentPhase === 0 ? (
          <div className="fade-in">
            <WaitingRoom
              user={user}
              setUser={setUser}
              isAdmin={isAdmin}
              goNext={goNextFunc}
            />
          </div>
        ) : isRedactionPhase() ? (
          <div className="fade-in">
            <Redaction
              user={user}
              setUser={setUser}
              phase={currentPhase}
              sujet={sujet}
              goNext={goNextFunc}
              setModalContent={setModalContent}
              setModalTitle={setModalTitle}
              setShowModal={setShowModal}
              parametres={parametres}
              type={type}
              redactionLongue={redactionLongue}
            />
          </div>
        ) : (
          <div className="fade-in">
            <Evaluation
              user={user}
              setUser={setUser}
              phase={currentPhase}
              phase_a_evaluer={currentPhase - 1}
              sujet={sujet}
              setShowModal={setShowModal}
              setModalTitle={setModalTitle}
              setModalContent={setModalContent}
              setEndSession={setEndSession}
              goNext={goNextFunc}
              closeSession={closeSessionFunc}
              parametres={parametres}
              type={type}
              redactionLongue={redactionLongue}
            />
          </div>
        )}
      </div>

      <SessionFooter
        user={user}
        setUser={setUser}
        phase={currentPhase}
        redaction={currentPhase === 0 ? false : isRedactionPhase()}
        currentPhase={currentPhase}
        setCurrentPhase={setCurrentPhase}
        dureeSession={getDurationForPhase()}
        dernieremaj={derniereMaj}
        goNext={goNextFunc}
        usersInRoom={usersInRoom}
        setUsersInRoom={setUsersInRoom}
        setModalContent={setModalContent}
        setModalTitle={setModalTitle}
        setShowModal={setShowModal}
        setEndSession={setEndSession}
        isWaitingRoom={currentPhase === 0}
      />

      {currentPhase >= 0 && (
        <SlidePanels
          user={user}
          usersInRoom={usersInRoom}
          redaction={currentPhase === 0 ? false : isRedactionPhase()}
          sujet={sujet}
          pastContributions={pastContributions}
          parametres={parametres}
          type={type}
          redactionLongue={redactionLongue}
          setModalContent={setModalContent}
          setModalTitle={setModalTitle}
          setShowModal={setShowModal}
          isWaitingRoom={currentPhase === 0}
        />
      )}

      {showAutoPodium && (
        <PodiumModal
          sessionInfo={autoPodiumSessionInfo}
          onClose={() => setShowAutoPodium(false)}
          type={type}
        />
      )}
    </>
  );
};

export default Session;